import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';

const GalleryContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 8px;
  background: #ffffff;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #ffffff;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.3s ease;
  cursor: pointer;

  ${GalleryContainer}:hover & {
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    object-fit: contain;
    object-position: center center;
  }
`;

const NavigationArrows = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 0 12px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${GalleryContainer}:hover & {
    opacity: 1;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const ArrowButton = styled.button`
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: all;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  color: #333;

  &:hover {
    background: #ff6b35;
    color: white;
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const DotsContainer = styled.div`
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  pointer-events: none;
`;

const Dot = styled.button`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: ${props => props.$active ? '#ff6b35' : 'rgba(255, 255, 255, 0.6)'};
  cursor: pointer;
  pointer-events: all;
  transition: all 0.2s ease;

  &:hover {
    background: #ff6b35;
    transform: scale(1.2);
  }
`;

const ImageCounter = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  pointer-events: none;
`;

const ProductImageGallery = ({ 
  images = [], 
  featuredImage, 
  alt = 'Товар',
  onImageClick,
  className 
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const galleryRef = useRef(null);

  // Объединяем featured_image с массивом images
  const allImages = React.useMemo(() => {
    const imageArray = [];
    
    // Добавляем featured_image первым, если он есть
    if (featuredImage) {
      imageArray.push(featuredImage);
    }
    
    // Добавляем остальные изображения, исключая дубликаты
    if (images && Array.isArray(images)) {
      images.forEach(img => {
        if (img !== featuredImage && !imageArray.includes(img)) {
          imageArray.push(img);
        }
      });
    }
    
    return imageArray;
  }, [images, featuredImage]);

  // Сброс индекса при изменении изображений
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [allImages]);

  const goToPrevious = useCallback(() => {
    setCurrentImageIndex(prev => 
      prev === 0 ? allImages.length - 1 : prev - 1
    );
  }, [allImages.length]);

  const goToNext = useCallback(() => {
    setCurrentImageIndex(prev => 
      prev === allImages.length - 1 ? 0 : prev + 1
    );
  }, [allImages.length]);

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  // Обработка свайпов для мобильных устройств
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && allImages.length > 1) {
      goToNext();
    }
    if (isRightSwipe && allImages.length > 1) {
      goToPrevious();
    }
  };

  // Обработка клика по изображению
  const handleImageClick = () => {
    if (onImageClick) {
      onImageClick(allImages[currentImageIndex], currentImageIndex);
    }
  };

  // Обработка клавиатуры
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (allImages.length <= 1) return;
      
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [allImages.length, goToPrevious, goToNext]);

  // Если нет изображений, показываем заглушку nofoto.png
  if (allImages.length === 0) {
    return (
      <GalleryContainer className={className}>
        <ImageContainer>
          <ProductImage 
            src="/nofoto.png" 
            alt={alt}
            style={{ objectFit: 'contain' }}
          />
        </ImageContainer>
      </GalleryContainer>
    );
  }

  return (
    <GalleryContainer 
      className={className}
      ref={galleryRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <ImageContainer>
        <ProductImage 
          src={allImages[currentImageIndex]} 
          alt={`${alt} - изображение ${currentImageIndex + 1}`}
          onClick={handleImageClick}
          onError={(e) => {
            // Предотвращаем бесконечный цикл
            if (!e.target.dataset.fallbackApplied) {
              e.target.dataset.fallbackApplied = 'true';
              e.target.style.display = 'none';
            }
          }}
        />
      </ImageContainer>

      {/* Стрелки навигации (только для десктопа) */}
      {allImages.length > 1 && (
        <NavigationArrows>
          <ArrowButton 
            onClick={goToPrevious}
            aria-label="Предыдущее изображение"
          >
            <svg viewBox="0 0 24 24">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
            </svg>
          </ArrowButton>
          <ArrowButton 
            onClick={goToNext}
            aria-label="Следующее изображение"
          >
            <svg viewBox="0 0 24 24">
              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
            </svg>
          </ArrowButton>
        </NavigationArrows>
      )}

      {/* Счетчик изображений */}
      {allImages.length > 1 && (
        <ImageCounter>
          {currentImageIndex + 1} / {allImages.length}
        </ImageCounter>
      )}

      {/* Точки навигации */}
      {allImages.length > 1 && (
        <DotsContainer>
          {allImages.map((_, index) => (
            <Dot
              key={index}
              $active={index === currentImageIndex}
              onClick={() => goToImage(index)}
              aria-label={`Перейти к изображению ${index + 1}`}
            />
          ))}
        </DotsContainer>
      )}
    </GalleryContainer>
  );
};

export default ProductImageGallery;
